import { useState, type FormEvent } from 'react';
import {
  useAppendFilesToVersionMutation,
  useCreateContractMutation,
  useUploadContractMutation,
} from '@shared/api';
import { isTooLargeAlone } from '@shared/lib/batchFiles';
import { ACCEPT_CONTRACT_SET } from '@shared/lib/fileAccept';
import {
  MAX_UPLOAD_BYTES,
  formatBytes,
  formatFileCount,
  totalSize,
} from '@shared/lib/formatFile';
import { CONTRACT_TYPE_OPTIONS } from '@shared/lib/statusLabels';
import { planBatches, sendBatches, type BatchProgress } from '@shared/lib/uploadBatches';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import { MultiFileList } from '@shared/ui/MultiFileList';
import { CounterpartyPicker } from '../CounterpartyPicker';
import {
  ContractFieldHint,
  Divider,
  UploadErrorBanner,
  UploadSizeHint,
} from './styles';
import type { Mode, RecordKind } from './types';

interface CreateContractModalProps {
  open: boolean;
  kind: RecordKind;
  title: string;
  canWrite: boolean;
  onClose: () => void;
}

export function CreateContractModal({
  open,
  kind,
  title,
  canWrite,
  onClose,
}: CreateContractModalProps) {
  const [createContract, { isLoading: isCreating }] = useCreateContractMutation();
  const [uploadContract, { isLoading: isUploading }] = useUploadContractMutation();
  const [appendFiles, { isLoading: isAppending }] = useAppendFilesToVersionMutation();

  const [mode, setMode] = useState<Mode>('upload');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [progress, setProgress] = useState<BatchProgress | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [source, setSource] = useState<'counterparty' | 'company'>('counterparty');

  const pickedBytes = totalSize(files);
  const batches = planBatches(files);
  const hugeFiles = files.filter(isTooLargeAlone);

  const [counterparty, setCounterparty] = useState('');
  const [counterpartyId, setCounterpartyId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [contractType, setContractType] = useState<string>('works');
  const [sum, setSum] = useState('');
  const [currency, setCurrency] = useState('RUB');

  function resetForm() {
    setFiles([]);
    setCounterparty('');
    setCounterpartyId(null);
    setSubject('');
    setContractType('works');
    setSum('');
    setCurrency('RUB');
    setUploadError(null);
    setProgress(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function buildFirstBatchForm(batch: File[], deferAnalysis: boolean): FormData {
    const form = new FormData();
    for (const f of batch) {
      form.append('file', f);
    }
    form.append('counterparty', counterparty);
    if (counterpartyId) form.append('counterparty_id', counterpartyId);
    if (subject) form.append('subject', subject);
    if (contractType) form.append('contract_type', contractType);
    form.append('source', source);
    form.append('record_kind', kind);
    if (deferAnalysis) form.append('defer_analysis', '1');
    return form;
  }

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    if (files.length === 0 || !counterparty.trim() || hugeFiles.length > 0) return;
    setUploadError(null);

    let target: { contractId: string; versionId: string } | null = null;
    const result = await sendBatches(
      batches,
      async (batch, i, total) => {
        if (i === 0) {
          const created = await uploadContract(buildFirstBatchForm(batch, total > 1)).unwrap();
          target = { contractId: created.contract_id, versionId: created.version_id };
          return;
        }
        const form = new FormData();
        for (const f of batch) form.append('file', f);
        if (i === total - 1) form.append('finalize', '1');
        await appendFiles({ ...target!, form }).unwrap();
      },
      setProgress,
      new AbortController().signal,
      (i) => i > 0,
    );
    setProgress(null);
    if (!result.error) {
      resetForm();
      onClose();
      return;
    }
    const err = result.error;
    const msg =
      (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
      (err as { error?: string })?.error ??
      'Не удалось загрузить договор. Попробуйте ещё раз или проверьте файл.';
    setUploadError(
      result.sent > 0
        ? `${msg} Договор создан, часть файлов загружена — откройте его карточку и догрузите остальное.`
        : msg,
    );
  }

  async function onCreateManual(e: FormEvent) {
    e.preventDefault();
    if (!counterparty.trim()) return;
    await createContract({
      counterparty,
      counterparty_id: counterpartyId ?? undefined,
      subject: subject || undefined,
      sum: sum ? Number(sum) : undefined,
      currency: currency || undefined,
      contract_type: contractType,
      record_kind: kind,
    }).unwrap();
    resetForm();
    onClose();
  }

  return (
    <Modal open={open} title={title} onClose={handleClose}>
      <Field>
        <Label htmlFor="mode">Способ создания</Label>
        <Select id="mode" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
          <option value="upload">Загрузить комплект документов — с LLM-экспертизой</option>
          <option value="manual">Только запись в реестре (без файла)</option>
        </Select>
      </Field>

      {mode === 'upload' ? (
        <form onSubmit={onUpload}>
          <Field>
            <Label>Файлы договора *</Label>
            <FileDropZone
              multiple
              accept={ACCEPT_CONTRACT_SET}
              hint="Можно добавить основной договор и все приложения/спецификации сразу"
              onFiles={(picked) =>
                setFiles((prev) => {
                  const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
                  const next = [...prev];
                  for (const f of picked) {
                    const key = `${f.name}::${f.size}`;
                    if (!seen.has(key)) {
                      next.push(f);
                      seen.add(key);
                    }
                  }
                  return next;
                })
              }
            />
            <MultiFileList
              files={files}
              onRemove={(idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))}
            />
            {files.length > 0 && (
              <UploadSizeHint $over={hugeFiles.length > 0}>
                {formatFileCount(files.length)}, {formatBytes(pickedBytes)}
                {batches.length > 1 && ` — загрузим ${batches.length} частями`}
                {hugeFiles.length > 0 && (
                  <>
                    {' — '}
                    {hugeFiles.length === 1
                      ? `файл «${hugeFiles[0]!.name}» весит ${formatBytes(hugeFiles[0]!.size)}`
                      : `${formatFileCount(hugeFiles.length)} весят больше`}{' '}
                    {`предела в ${formatBytes(MAX_UPLOAD_BYTES)} на файл. Такой файл не разделить — уберите его из комплекта.`}
                  </>
                )}
              </UploadSizeHint>
            )}
          </Field>
          <Field>
            <Label htmlFor="source">Источник версии</Label>
            <Select
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value as 'counterparty' | 'company')}
            >
              <option value="counterparty">От контрагента</option>
              <option value="company">Наша редакция</option>
            </Select>
          </Field>
          <CounterpartyPicker
            counterpartyId={counterpartyId}
            counterpartyName={counterparty}
            onChange={({ id, name }) => {
              setCounterpartyId(id);
              setCounterparty(name);
            }}
          />
          <Field>
            <Label htmlFor="subject">Предмет договора (опц.)</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="LLM попробует извлечь сам, если оставить пустым"
            />
          </Field>
          <Field>
            <Label htmlFor="ctype">Тип договора</Label>
            <Select
              id="ctype"
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
            >
              {CONTRACT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <ContractFieldHint>
              Если для этого типа загружена корпоративная форма САТКОМ — LLM дополнительно сверит
              договор с ней и пометит отклонения.
            </ContractFieldHint>
          </Field>
          {uploadError && (
            <UploadErrorBanner>
              <strong>Ошибка загрузки:</strong> {uploadError}
            </UploadErrorBanner>
          )}
          <Button
            type="submit"
            disabled={
              progress !== null ||
              isUploading ||
              isAppending ||
              files.length === 0 ||
              hugeFiles.length > 0 ||
              !canWrite
            }
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {progress
              ? `${progress.retry ? 'Связь прервалась, повторяем' : 'Загрузка'}… ` +
                `${formatBytes(progress.bytesDone)} из ${formatBytes(progress.bytesTotal)}`
              : isUploading || isAppending
                ? 'Загрузка и анализ…'
                : files.length > 1
                  ? `Загрузить ${formatFileCount(files.length)} и проанализировать`
                  : 'Загрузить и проанализировать'}
          </Button>
        </form>
      ) : (
        <form onSubmit={onCreateManual}>
          <Divider>
            <span>ручная регистрация</span>
          </Divider>
          <CounterpartyPicker
            counterpartyId={counterpartyId}
            counterpartyName={counterparty}
            onChange={({ id, name }) => {
              setCounterpartyId(id);
              setCounterparty(name);
            }}
          />
          <Field>
            <Label htmlFor="subject">Предмет договора</Label>
            <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field>
            <Label htmlFor="sum">Сумма</Label>
            <Input id="sum" type="number" value={sum} onChange={(e) => setSum(e.target.value)} />
          </Field>
          <Field>
            <Label htmlFor="currency">Валюта</Label>
            <Input id="currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
          </Field>
          <Button
            type="submit"
            disabled={isCreating || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            Создать
          </Button>
        </form>
      )}
    </Modal>
  );
}
